import { trpc } from "@/app/router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { withRouteProtection } from "@/lib/router/route-protection";
import { PROTECTION_PRESETS } from "@/lib/router/protection-presets";
import { AdminGate } from "@/lib/components/rbac";
import { Check, Pencil, Plus, RefreshCw, Search, User, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Button } from "shadcn-ui";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "shadcn-ui";
import { Textarea } from "shadcn-ui";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "shadcn-ui";
import { Input } from "shadcn-ui";
import { Label } from "shadcn-ui";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "shadcn-ui";
import { Checkbox } from "shadcn-ui";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "shadcn-ui";
import { Badge } from "shadcn-ui";
import { ScrollArea } from "shadcn-ui";
import { Separator } from "shadcn-ui";

export const Route = createFileRoute("/_app/admin")({
	component: RouteComponent,
	...withRouteProtection(PROTECTION_PRESETS.ADMIN_ONLY),
});

// Import the types from the schema
import type { Permission, Role } from "trpc-yehey_live/src/auth/schemas";

interface ClerkUser {
	id: string;
	firstName: string | null;
	lastName: string | null;
	emailAddresses: Array<{ emailAddress: string }>;
	imageUrl: string;
	permissions: Permission[];
	roles: Role[];
}

function UserManagement() {
	const [users, setUsers] = useState<ClerkUser[]>([]);
	const [searchQuery, setSearchQuery] = useState("");
	const [isPending, setIsPending] = useState(true);
	const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
	const [isPermissionDialogOpen, setIsPermissionDialogOpen] = useState(false);
	const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);

	// Get all available roles and permissions
	const { data: authSchema } = useQuery(
		trpc.auth.getAuthorizationSchema.queryOptions(),
	);

	// Fetch users from Clerk
	const { mutate: fetchUsers, isPending: isFetchingUsers } = useMutation(
		trpc.auth.getAllUsers.mutationOptions({
			onSuccess: (data) => {
				setUsers(data.users);
				setIsPending(false);
			},
			onError: (error) => {
				console.error("Error fetching users:", error);
				setIsPending(false);
			},
		}),
	);

	// Update user permissions
	const { mutate: updatePermissions, isPending: isUpdatingPermissions } =
		useMutation(
			trpc.auth.updatePermissions.mutationOptions({
				onSuccess: () => {
					// Refresh user list after update
					fetchUsers();
					setIsPermissionDialogOpen(false);
				},
			}),
		);

	// Update user roles
	const { mutate: updateRoles, isPending: isUpdatingRoles } = useMutation(
		trpc.auth.updateRoles.mutationOptions({
			onSuccess: () => {
				// Refresh user list after update
				fetchUsers();
				setIsRoleDialogOpen(false);
			},
		}),
	);

	// Initialize user with default permissions
	const { mutate: initializeUser } = useMutation(
		trpc.auth.initializeUserAuthorization.mutationOptions({
			onSuccess: () => {
				// Refresh user list after initialization
				fetchUsers();
			},
		}),
	);

	useEffect(() => {
		// Fetch users when component mounts
		fetchUsers();
	}, []);

	// Filter users based on search query
	const filteredUsers = users.filter((user) => {
		const fullName =
			`${user.firstName || ""} ${user.lastName || ""}`.toLowerCase();
		const email = user.emailAddresses[0]?.emailAddress.toLowerCase() || "";
		const query = searchQuery.toLowerCase();
		return fullName.includes(query) || email.includes(query);
	});

	// Get selected user
	const selectedUser = users.find((user) => user.id === selectedUserId);

	return (
		<div className="space-y-4">
			<div className="flex justify-between items-center">
				<h2 className="text-2xl font-bold">User Management</h2>
				<div className="flex gap-2">
					<div className="relative">
						<Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
						<Input
							type="search"
							placeholder="Search users..."
							className="pl-8 w-[250px]"
							value={searchQuery}
							onChange={(e) => setSearchQuery(e.target.value)}
						/>
					</div>
					<Button
						variant="outline"
						size="icon"
						onClick={() => fetchUsers()}
						disabled={isFetchingUsers}
					>
						<RefreshCw
							className={`h-4 w-4 ${isFetchingUsers ? "animate-spin" : ""}`}
						/>
					</Button>
				</div>
			</div>

			{isPending ? (
				<div className="flex justify-center items-center h-64">
					<RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
				</div>
			) : (
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
					{filteredUsers.map((user) => (
						<Card key={user.id} className="overflow-hidden">
							<CardHeader className="pb-2">
								<div className="flex justify-between items-start">
									<div className="flex items-center gap-2">
										{user.imageUrl ? (
											<img
												src={user.imageUrl}
												alt={`${user.firstName} ${user.lastName}`}
												className="h-8 w-8 rounded-full object-cover"
											/>
										) : (
											<div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
												<User className="h-4 w-4 text-muted-foreground" />
											</div>
										)}
										<div>
											<CardTitle className="text-base">
												{user.firstName} {user.lastName}
											</CardTitle>
											<CardDescription className="text-xs truncate max-w-[200px]">
												{user.emailAddresses[0]?.emailAddress}
											</CardDescription>
										</div>
									</div>
								</div>
							</CardHeader>
							<CardContent>
								<div className="space-y-2">
									<div>
										<div className="flex justify-between items-center mb-1">
											<Label className="text-xs font-medium">Roles</Label>
											<Button
												variant="ghost"
												size="sm"
												className="h-6 px-2"
												onClick={() => {
													setSelectedUserId(user.id);
													setIsRoleDialogOpen(true);
												}}
											>
												<Plus className="h-3 w-3 mr-1" />
												<span className="text-xs">Manage</span>
											</Button>
										</div>
										<div className="flex flex-wrap gap-1">
											{user.roles.length ? (
												user.roles.map((role) => (
													<Badge
														key={role}
														variant="outline"
														className="text-xs"
													>
														{role}
													</Badge>
												))
											) : (
												<Button
													variant="outline"
													size="sm"
													className="h-6 px-2 text-xs"
													onClick={() =>
														initializeUser({
															userId: user.id,
															defaultRole: "readonly",
														})
													}
												>
													Initialize with readonly
												</Button>
											)}
										</div>
									</div>
									<div>
										<div className="flex justify-between items-center mb-1">
											<Label className="text-xs font-medium">Permissions</Label>
											<Button
												variant="ghost"
												size="sm"
												className="h-6 px-2"
												onClick={() => {
													setSelectedUserId(user.id);
													setIsPermissionDialogOpen(true);
												}}
												disabled={!user.roles.length}
											>
												<Plus className="h-3 w-3 mr-1" />
												<span className="text-xs">Manage</span>
											</Button>
										</div>
										<div className="flex flex-wrap gap-1 max-h-[60px] overflow-y-auto">
											{user.permissions.length ? (
												user.permissions.slice(0, 5).map((permission) => (
													<Badge
														key={permission}
														variant="secondary"
														className="text-xs"
													>
														{permission}
													</Badge>
												))
											) : (
												<span className="text-xs text-muted-foreground">
													No permissions
												</span>
											)}
											{user.permissions.length > 5 && (
												<Badge variant="secondary" className="text-xs">
													+{user.permissions.length - 5} more
												</Badge>
											)}
										</div>
									</div>
								</div>
							</CardContent>
						</Card>
					))}
				</div>
			)}

			{/* Role Management Dialog */}
			<Dialog open={isRoleDialogOpen} onOpenChange={setIsRoleDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Manage User Roles</DialogTitle>
						<DialogDescription>
							Assign roles to {selectedUser?.firstName} {selectedUser?.lastName}
						</DialogDescription>
					</DialogHeader>

					{selectedUser && (
						<RoleManager
							user={selectedUser}
							availableRoles={authSchema?.roles || []}
							onSave={(roles) => {
								updateRoles({
									userId: selectedUser.id,
									roles: roles as (
										| "admin"
										| "manager"
										| "inventory_manager"
										| "sales_representative"
										| "purchase_manager"
										| "warehouse_staff"
										| "readonly"
									)[],
									operation: "set",
								});
							}}
							isPending={isUpdatingRoles}
						/>
					)}
				</DialogContent>
			</Dialog>

			{/* Permission Management Dialog */}
			<Dialog
				open={isPermissionDialogOpen}
				onOpenChange={setIsPermissionDialogOpen}
			>
				<DialogContent className="max-w-2xl">
					<DialogHeader>
						<DialogTitle>Manage User Permissions</DialogTitle>
						<DialogDescription>
							Customize permissions for {selectedUser?.firstName}{" "}
							{selectedUser?.lastName}
						</DialogDescription>
					</DialogHeader>

					{selectedUser && authSchema && (
						<PermissionManager
							user={selectedUser}
							rolePermissions={authSchema.rolePermissions}
							onSave={(permissions) => {
								updatePermissions({
									userId: selectedUser.id,
									permissions,
									operation: "set",
								});
							}}
							isPending={isUpdatingPermissions}
						/>
					)}
				</DialogContent>
			</Dialog>
		</div>
	);
}

interface RoleManagerProps {
	user: ClerkUser;
	availableRoles: (
		| "admin"
		| "manager"
		| "inventory_manager"
		| "sales_representative"
		| "purchase_manager"
		| "warehouse_staff"
		| "readonly"
	)[];
	onSave: (
		roles: (
			| "admin"
			| "manager"
			| "inventory_manager"
			| "sales_representative"
			| "purchase_manager"
			| "warehouse_staff"
			| "readonly"
		)[],
	) => void;
	isPending: boolean;
}

function RoleManager({
	user,
	availableRoles,
	onSave,
	isPending,
}: RoleManagerProps) {
	const [selectedRoles, setSelectedRoles] = useState<Role[]>(user.roles || []);

	const toggleRole = (role: string) => {
		if (selectedRoles.includes(role)) {
			setSelectedRoles(selectedRoles.filter((r) => r !== role));
		} else {
			// setSelectedRoles([...selectedRoles, role]);
		}
	};

	return (
		<>
			<div className="grid gap-4 py-4">
				{availableRoles.map((role) => (
					<div key={role} className="flex items-center space-x-2">
						<Checkbox
							id={`role-${role}`}
							checked={selectedRoles.includes(role)}
							onCheckedChange={() => toggleRole(role)}
						/>
						<Label htmlFor={`role-${role}`} className="capitalize">
							{role.replace("_", " ")}
						</Label>
					</div>
				))}
			</div>
			<DialogFooter>
				<Button
					variant="outline"
					onClick={() => setSelectedRoles(user.roles || [])}
				>
					Reset
				</Button>
				<Button onClick={() => onSave(selectedRoles)} disabled={isPending}>
					{isPending ? "Saving..." : "Save changes"}
				</Button>
			</DialogFooter>
		</>
	);
}

interface PermissionManagerProps {
	user: ClerkUser;
	rolePermissions: Record<string, string[]>;
	onSave: (permissions: string[]) => void;
	isPending: boolean;
}

function PermissionManager({
	user,
	rolePermissions,
	onSave,
	isPending,
}: PermissionManagerProps) {
	const [selectedPermissions, setSelectedPermissions] = useState<Permission[]>(
		user.permissions || [],
	);
	const [activeTab, setActiveTab] = useState<string>("custom");

	// Group permissions by category
	const permissionsByCategory: Record<string, string[]> = {};

	// Collect all unique permissions from role permissions
	const allPermissions = new Set<string>();
	for (const permissions of Object.values(rolePermissions)) {
		for (const permission of permissions) {
			allPermissions.add(permission);

			// Group by category (split by dot)
			const [category] = permission.split(".");
			if (!permissionsByCategory[category]) {
				permissionsByCategory[category] = [];
			}
			if (!permissionsByCategory[category].includes(permission)) {
				permissionsByCategory[category].push(permission);
			}
		}
	}

	const togglePermission = (permission: string) => {
		if (selectedPermissions.includes(permission)) {
			setSelectedPermissions(
				selectedPermissions.filter((p) => p !== permission),
			);
		} else {
			setSelectedPermissions([...selectedPermissions, permission]);
		}
	};

	const selectAllInCategory = (category: string) => {
		const categoryPermissions = permissionsByCategory[category];
		const newPermissions = [...selectedPermissions];

		for (const permission of categoryPermissions) {
			if (!newPermissions.includes(permission)) {
				newPermissions.push(permission);
			}
		}

		setSelectedPermissions(newPermissions);
	};

	const deselectAllInCategory = (category: string) => {
		const categoryPermissions = permissionsByCategory[category];
		setSelectedPermissions(
			selectedPermissions.filter((p) => !categoryPermissions.includes(p)),
		);
	};

	// Check if all permissions in a category are selected
	const isCategoryFullySelected = (category: string) => {
		return permissionsByCategory[category].every((p) =>
			selectedPermissions.includes(p),
		);
	};

	// Check if some permissions in a category are selected
	const isCategoryPartiallySelected = (category: string) => {
		const hasSelected = permissionsByCategory[category].some((p) =>
			selectedPermissions.includes(p),
		);
		return hasSelected && !isCategoryFullySelected(category);
	};

	return (
		<>
			<Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
				<TabsList className="grid w-full grid-cols-2">
					<TabsTrigger value="custom">Custom Permissions</TabsTrigger>
					<TabsTrigger value="template">Role Templates</TabsTrigger>
				</TabsList>

				<TabsContent value="custom" className="space-y-4">
					<ScrollArea className="h-[300px] pr-4">
						{Object.keys(permissionsByCategory).map((category) => (
							<div key={category} className="mb-4">
								<div className="flex items-center justify-between mb-2">
									<div className="flex items-center space-x-2">
										<Checkbox
											id={`category-${category}`}
											checked={isCategoryFullySelected(category)}
											indeterminate={isCategoryPartiallySelected(category)}
											onCheckedChange={(checked) => {
												if (checked) {
													selectAllInCategory(category);
												} else {
													deselectAllInCategory(category);
												}
											}}
										/>
										<Label
											htmlFor={`category-${category}`}
											className="text-sm font-medium capitalize"
										>
											{category.replace("_", " ")}
										</Label>
									</div>
									<div className="text-xs text-muted-foreground">
										{
											permissionsByCategory[category].filter((p) =>
												selectedPermissions.includes(p),
											).length
										}{" "}
										/ {permissionsByCategory[category].length}
									</div>
								</div>

								<div className="ml-6 space-y-1">
									{permissionsByCategory[category].map((permission) => {
										const [, action] = permission.split(".");
										return (
											<div
												key={permission}
												className="flex items-center space-x-2"
											>
												<Checkbox
													id={`permission-${permission}`}
													checked={selectedPermissions.includes(permission)}
													onCheckedChange={() => togglePermission(permission)}
												/>
												<Label
													htmlFor={`permission-${permission}`}
													className="text-sm capitalize"
												>
													{action.replace("_", " ")}
												</Label>
											</div>
										);
									})}
								</div>
								<Separator className="my-2" />
							</div>
						))}
					</ScrollArea>
				</TabsContent>

				<TabsContent value="template">
					<div className="space-y-4">
						<div className="grid gap-2">
							<Label htmlFor="role-template">Select a role template</Label>
							<Select
								onValueChange={(value) => {
									setSelectedPermissions([...rolePermissions[value]]);
								}}
							>
								<SelectTrigger id="role-template">
									<SelectValue placeholder="Select a role template" />
								</SelectTrigger>
								<SelectContent>
									{Object.keys(rolePermissions).map((role) => (
										<SelectItem key={role} value={role} className="capitalize">
											{role.replace("_", " ")}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>

						<div className="rounded-md border p-4">
							<div className="flex justify-between items-center mb-2">
								<h4 className="text-sm font-medium">Selected Permissions</h4>
								<Badge variant="secondary">{selectedPermissions.length}</Badge>
							</div>
							<ScrollArea className="h-[200px]">
								<div className="space-y-1">
									{selectedPermissions.length === 0 ? (
										<p className="text-sm text-muted-foreground">
											No permissions selected
										</p>
									) : (
										selectedPermissions.map((permission) => (
											<div
												key={permission}
												className="flex items-center justify-between group"
											>
												<span className="text-sm">{permission}</span>
												<Button
													variant="ghost"
													size="sm"
													className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100"
													onClick={() => togglePermission(permission)}
												>
													<X className="h-3 w-3" />
												</Button>
											</div>
										))
									)}
								</div>
							</ScrollArea>
						</div>
					</div>
				</TabsContent>
			</Tabs>

			<DialogFooter>
				<Button
					variant="outline"
					onClick={() => setSelectedPermissions(user.permissions || [])}
				>
					Reset
				</Button>
				<Button
					onClick={() => onSave(selectedPermissions)}
					disabled={isPending}
				>
					{isPending ? "Saving..." : "Save changes"}
				</Button>
			</DialogFooter>
		</>
	);
}

function RolePermissionManagement() {
	// Get schema for predefined roles and permissions

	// Get actual roles and permissions from database
	const { data: dbRoles } = useQuery(trpc.auth.getRoles.queryOptions());
	const { data: dbPermissions } = useQuery(
		trpc.auth.getPermissions.queryOptions(),
	);

	const [selectedRole, setSelectedRole] = useState<string | null>(null);
	const [isAddRoleDialogOpen, setIsAddRoleDialogOpen] = useState(false);
	const [isEditRoleDialogOpen, setIsEditRoleDialogOpen] = useState(false);

	return (
		<div className="space-y-6">
			<div className="flex justify-between items-center">
				<h2 className="text-2xl font-bold">Roles & Permissions</h2>
				<Button onClick={() => setIsAddRoleDialogOpen(true)}>
					<Plus className="h-4 w-4 mr-2" />
					Add Role
				</Button>
			</div>

			{/* Role list */}
			<div className="grid gap-4">
				{dbRoles?.map((role) => (
					<Card key={role.id}>
						<CardHeader className="pb-2">
							<div className="flex justify-between items-center">
								<CardTitle className="capitalize">
									{role.name.replace("_", " ")}
								</CardTitle>
								<Button
									variant="ghost"
									size="sm"
									onClick={() => {
										setSelectedRole(role.id);
										setIsEditRoleDialogOpen(true);
									}}
								>
									<Pencil className="h-4 w-4" />
								</Button>
							</div>
						</CardHeader>
						<CardContent>
							<div className="text-sm text-muted-foreground mb-4">
								{role.description || getRoleDescription(role.name)}
							</div>
							<div className="space-y-2">
								<h4 className="text-sm font-medium">Permissions:</h4>
								<div className="flex flex-wrap gap-1">
									{role.permissions?.map((permission) => (
										<Badge
											key={permission.id}
											variant="secondary"
											className="text-xs"
										>
											{permission.name}
										</Badge>
									))}
								</div>
							</div>
						</CardContent>
					</Card>
				))}
			</div>

			{/* Add Role Dialog */}
			<Dialog open={isAddRoleDialogOpen} onOpenChange={setIsAddRoleDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Add New Role</DialogTitle>
						<DialogDescription>
							Create a new role with custom permissions
						</DialogDescription>
					</DialogHeader>
					{/* Role creation form will go here */}
					<div className="grid gap-4 py-4">
						<div className="grid gap-2">
							<Label htmlFor="role-name">Role Name</Label>
							<Input id="role-name" placeholder="Enter role name" />
						</div>
						<div className="grid gap-2">
							<Label htmlFor="role-description">Description</Label>
							<Textarea
								id="role-description"
								placeholder="Enter role description"
							/>
						</div>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setIsAddRoleDialogOpen(false)}
						>
							Cancel
						</Button>
						<Button>Create Role</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Edit Role Dialog */}
			<Dialog
				open={isEditRoleDialogOpen}
				onOpenChange={setIsEditRoleDialogOpen}
			>
				<DialogContent className="max-w-2xl">
					<DialogHeader>
						<DialogTitle>
							Edit{" "}
							{dbRoles
								?.find((r) => r.id === selectedRole)
								?.name.replace("_", " ")}
						</DialogTitle>
						<DialogDescription>
							Modify role permissions and settings
						</DialogDescription>
					</DialogHeader>
					{/* Permission management for role will go here */}
					<div className="grid gap-4 py-4">
						<div className="grid gap-2">
							<Label>Role Permissions</Label>
							<div className="border rounded-md p-4 max-h-[300px] overflow-y-auto">
								{dbPermissions ? (
									<div className="grid grid-cols-2 gap-2">
										{dbPermissions.map((permission) => {
											const selectedRoleObj = dbRoles?.find(
												(r) => r.id === selectedRole,
											);
											const isChecked = selectedRoleObj?.permissions.some(
												(p) => p.id === permission.id,
											);

											return (
												<div
													key={permission.id}
													className="flex items-center space-x-2"
												>
													<Checkbox
														id={`perm-${permission.id}`}
														checked={isChecked}
													/>
													<Label
														htmlFor={`perm-${permission.id}`}
														className="text-sm"
													>
														{permission.name}
													</Label>
												</div>
											);
										})}
									</div>
								) : (
									<div className="text-center py-4 text-muted-foreground">
										Loading permissions...
									</div>
								)}
							</div>
						</div>
					</div>
					<DialogFooter>
						<Button
							variant="outline"
							onClick={() => setIsEditRoleDialogOpen(false)}
						>
							Cancel
						</Button>
						<Button>Save Changes</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}

// Helper function to get role descriptions
function getRoleDescription(role: string): string {
	const descriptions: Record<string, string> = {
		admin: "Full system access with all permissions",
		manager: "Comprehensive system management capabilities",
		inventory_manager: "Manage inventory, products, and stock levels",
		sales_representative: "Handle sales orders and customer interactions",
		purchase_manager: "Manage purchase orders and vendor relationships",
		warehouse_staff: "Basic inventory viewing and stock operations",
		readonly: "View-only access across all modules",
	};

	return descriptions[role] || "Custom role";
}

function RouteComponent() {
	return (
		<div className="container p-6">
			<AdminGate
				fallback={
					<div className="text-center py-10">
						You need admin privileges to access this page.
					</div>
				}
			>
				<Tabs defaultValue="users">
					<TabsList>
						<TabsTrigger value="users">Users</TabsTrigger>
						<TabsTrigger value="roles">Roles & Permissions</TabsTrigger>
						<TabsTrigger value="settings">System Settings</TabsTrigger>
					</TabsList>
					<TabsContent value="users" className="mt-4">
						<UserManagement />
					</TabsContent>
					<TabsContent value="roles" className="mt-4">
						<RolePermissionManagement />
					</TabsContent>
					<TabsContent value="settings" className="mt-4">
						<div className="text-center py-10 text-muted-foreground">
							System settings will be implemented in a future update.
						</div>
					</TabsContent>
				</Tabs>
			</AdminGate>
		</div>
	);
}
